/**
 * ClockOutConfirm — one-big-button clock-out with a shift summary
 * (Aug 2026). Shows time in, elapsed, break totals and the work tag,
 * plus a Site-QR scan alternative. Confirmation replaces window.confirm.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { LogOut, Loader2, Clock, Coffee, MapPin } from 'lucide-react';
import { QrPunchButton } from './QrPunch';

const API = `${window.__BACKEND_URL__}/api`;

const utcDate = (v) => new Date(typeof v === 'string' && !/[zZ]|[+-]\d\d:?\d\d$/.test(v) ? `${v}Z` : v);

export const ClockOutConfirm = ({ token, open, clockStatus, elapsed, busy, onConfirm, onQrDone, onClose }) => {
  const [breaks, setBreaks] = useState(null);

  useEffect(() => {
    if (!open || !token) return;
    axios.get(`${API}/time/break/current`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setBreaks(data.today || {}))
      .catch(() => setBreaks({}));
  }, [open, token]);

  if (!open) return null;
  const inTime = clockStatus?.clock_in_time
    ? utcDate(clockStatus.clock_in_time).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : '—';
  const paidMin = breaks?.paid_minutes || 0;
  const unpaidMin = breaks?.unpaid_minutes || 0;
  const tag = clockStatus?.area_label || clockStatus?.job_number || clockStatus?.project_number;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[95vw] sm:max-w-sm" data-testid="clock-out-confirm">
        <DialogHeader>
          <DialogTitle>End your shift?</DialogTitle>
          <DialogDescription>Here's your day so far.</DialogDescription>
        </DialogHeader>

        <div className="space-y-2" data-testid="shift-summary">
          <div className="flex items-center justify-between rounded-xl px-4 py-3" style={{ background: '#0B1321', border: '1px solid #2D3748' }}>
            <span className="flex items-center gap-2 text-sm" style={{ color: '#A0AEC0' }}>
              <Clock className="w-4 h-4" style={{ color: '#1E88E5' }} /> Clocked in
            </span>
            <span className="text-sm font-semibold text-white" data-testid="summary-in-time">{inTime}</span>
          </div>
          <div className="flex items-center justify-between rounded-xl px-4 py-3" style={{ background: '#0B1321', border: '1px solid #2D3748' }}>
            <span className="flex items-center gap-2 text-sm" style={{ color: '#A0AEC0' }}>
              <Clock className="w-4 h-4" style={{ color: '#00C896' }} /> Time worked
            </span>
            <span className="text-sm font-bold" style={{ color: '#00C896' }} data-testid="summary-elapsed">{elapsed}</span>
          </div>
          <div className="flex items-center justify-between rounded-xl px-4 py-3" style={{ background: '#0B1321', border: '1px solid #2D3748' }}>
            <span className="flex items-center gap-2 text-sm" style={{ color: '#A0AEC0' }}>
              <Coffee className="w-4 h-4" style={{ color: '#60A5FA' }} /> Breaks
            </span>
            <span className="text-sm text-white" data-testid="summary-breaks">
              {breaks === null ? '…' : `${paidMin}m paid · ${unpaidMin}m unpaid`}
            </span>
          </div>
          {tag && (
            <div className="flex items-center justify-between rounded-xl px-4 py-3" style={{ background: '#0B1321', border: '1px solid #2D3748' }}>
              <span className="flex items-center gap-2 text-sm" style={{ color: '#A0AEC0' }}>
                <MapPin className="w-4 h-4" style={{ color: '#FBBF24' }} /> Working at
              </span>
              <span className="text-sm text-white truncate max-w-[50%]" data-testid="summary-tag">{tag}</span>
            </div>
          )}
        </div>

        {/* THE one primary action */}
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          className="w-full rounded-2xl text-white font-bold text-lg flex items-center justify-center gap-2 transition active:scale-95 disabled:opacity-50"
          style={{ minHeight: '64px', background: 'linear-gradient(135deg, #EF4444, #DC2626)', boxShadow: '0 8px 28px rgba(239,68,68,0.3)' }}
          data-testid="confirm-clock-out-btn"
        >
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><LogOut className="w-5 h-5" /> CLOCK OUT</>}
        </button>

        <QrPunchButton token={token} clockedIn onDone={onQrDone} />

        <button
          type="button"
          onClick={onClose}
          className="w-full py-3 text-sm font-medium"
          style={{ color: '#64748B' }}
          data-testid="cancel-clock-out-btn"
        >
          Keep working
        </button>
      </DialogContent>
    </Dialog>
  );
};

export default ClockOutConfirm;
